// The SEEP panel — metocean context for a geochemical seep-hunting station.
//
// Seep hunting, as TDI-Brooks runs it, is three things done from one deck:
// multibeam mapping (including WATER-COLUMN imaging, where a gas flare shows as
// a plume above the seabed), piston coring on the targets that mapping picks
// out, and heat-flow measurement at the same stations. Each of the three has a
// metocean dependency that this console already holds a number for, and one that
// it does not. Both are stated.
//
// THE THREE DEPENDENCIES
//
//  - MBES water-column imaging depends on the sound-speed profile, and
//    specifically on its STRATIFICATION rather than its mean. Flares are found
//    in the outer beams, and the outer beams are exactly where refraction error
//    from an unresolved gradient throws a return off position. The console
//    already computes that profile — globally, to full depth; this panel says
//    what it means for the survey rather than for the acoustics.
//
//  - Heat flow depends on the bottom-water temperature NOT having moved
//    recently, because a transient at the seabed diffuses into the sediment and
//    tilts the geothermal gradient the probe is there to measure. The published
//    figure is 2-10% uncertainty in the gradient from annual bottom-water
//    variability, modelled from swings of ±0.03-0.15 °C. So the absolute is the
//    less interesting half: what matters is whether the bottom water has MOVED.
//    Below the Yucatán sill that is measured and reassuringly small (2-4 mK);
//    on the Guyana margin and in the Angola Basin it is simply unmeasured, and
//    the panel says which of the two it is looking at.
//
//  - Coring depends on the water column between the winch and the target, and
//    that is the gap: the console carries SURFACE current only. In the deepwater
//    Gulf a Loop Current eddy runs to 500-1000 m and up to 4 kn, so the surface
//    number can be the least interesting part of the column. The panel names
//    this rather than letting a surface figure stand in for a profile.
//
// WHAT IT IS NOT. It does not pick targets, plan a line, or interpret a core.
// It is the same bargain the SVP panel makes: planning-grade context, gaps
// named, no verdict on anyone's science.

import type { PointForecast } from "../forecast/openmeteo.js";
import type { SvpResult } from "../data/api.js";
import { compass16 } from "../data/stormlist.js";
import { fmtDepth, lenUnit, convLen } from "../ui/units.js";
import type { Fact } from "./factpanel.js";

/** Deepest level in the stored profile, and its temperature. */
function profileFloor(svp: SvpResult): { depth: number; tempC: number } | null {
  const rows = svp.cell.p.filter((r) => r.length >= 2 && Number.isFinite(r[0]) && Number.isFinite(r[1]));
  if (rows.length === 0) return null;
  const last = rows[rows.length - 1];
  return { depth: last[0], tempC: last[1] };
}

/**
 * Did the modelled column actually reach the seabed?
 *
 * The model ANSWERS THIS ITSELF now, and that is the whole improvement. GLO12 is
 * a pure z-level model fetched to full depth, so a profile ends where the cell's
 * bathymetry does, and the fetch records that as `sb`. Where `sb` is present the
 * deepest level IS bottom water — no inference, no tolerance.
 *
 * The fallback exists only for a grid baked before `sb` was added, and it is a
 * fudge: it compares our deepest level against a CHARTED depth from a different
 * survey, and those disagree by a bias — the model runs of order 140 m deeper —
 * rather than by noise. A bias is not something a tolerance can be widened to
 * absorb, which is why this is a fallback and not the method.
 *
 * (Two earlier versions of this file were wrong here in different ways: one used
 * 60 m, which no station could pass; and the same version told the client the
 * grid stopped at 1,000 m, which it had not for months. The lesson both times
 * was to ask the data what it knows instead of inferring it from a second source.)
 */
const LEGACY_TOLERANCE_M = 250;

/**
 * The deep Gulf below the Yucatán sill, where the bottom-water term is MEASURED.
 *
 * The sill is at about 2,040 m (Amon et al. 2023, Sci. Adv. 9:eade1685). Below it
 * the basin is a ~100-year-residence, effectively isothermal reservoir, and the
 * UGOS CPIES array (24 sites, hourly, 2019–21, CC0) puts near-bottom variability
 * at 2–4 mK RMS with an annual harmonic under 1 mK. That is one to two ORDERS OF
 * MAGNITUDE below the ±0.03–0.15 °C band the 2–10% heat-flow uncertainty is
 * built on — good news, and specific enough to say out loud.
 *
 * It is also strictly local. The Guyana/Suriname margin and the Angola Basin
 * have zero Deep Argo floats and no equivalent public mooring array, so the
 * number cannot be carried there and the panel does not carry it.
 */
const GOM_SILL_M = 2040;
function inDeepGom(lat: number, lon: number, depthM: number): boolean {
  return depthM >= GOM_SILL_M && lat >= 18 && lat <= 31 && lon >= -98 && lon <= -80;
}

export interface SeepInput {
  fc: PointForecast;
  svp: SvpResult | null;
  /** True when the SVP endpoint answered for this point (vs. outside coverage). */
  svpCovered: boolean;
  /** Index into fc.times for the hour under the playhead. */
  i: number;
}

export function seepFacts(input: SeepInput): Fact[] {
  const { fc, svp, svpCovered, i } = input;
  const facts: Fact[] = [];

  // --- the seabed, which is the thing being sampled -------------------------
  const depth = fc.depthM;
  facts.push({
    label: "Water depth",
    value: depth === null ? null : fmtDepth(depth),
    absent: "no charted depth at this point",
    why: "Wire out, core recovery expectations, and whether the modelled column below reaches the seabed at all.",
  });

  const relief = fc.relief;
  facts.push({
    label: "Seabed relief (~5 km)",
    value:
      relief === null
        ? null
        : `${fmtDepth(relief.shallow)} – ${fmtDepth(relief.deep)} (${fmtDepth(relief.deep - relief.shallow)} range)`,
    absent: "no bathymetric statistics here",
    why: "Flat ground cores and lands a heat-flow probe cleanly; strong relief is where a target is worth mapping twice before committing wire.",
    caution: relief !== null && relief.deep - relief.shallow > 200,
  });

  // --- MBES water-column imaging -------------------------------------------
  if (!svpCovered) {
    facts.push({
      label: "Sound-speed structure",
      value: null,
      absent: "no model cell at this point",
      why: "Without it there is nothing to say about outer-beam refraction, which is where flares are detected.",
      caution: true,
    });
  } else if (svp === null) {
    facts.push({
      label: "Sound-speed structure",
      value: null,
      absent: "profile did not load",
      why: "Retry, or read it in the SVP workspace.",
      caution: true,
    });
  } else {
    const c = svp.cell;
    const th = c.th;
    facts.push({
      label: "Surface sound speed",
      value: `${c.cs.toFixed(1)} m/s`,
      why: "The transducer-face value the MBES starts from; the profile below is what bends the beams.",
    });
    facts.push({
      label: "Strongest gradient",
      value:
        th === null
          ? "well mixed — no marked thermocline"
          : `${fmtDepth(th[0])} – ${fmtDepth(th[1])}, ${th[2].toFixed(1)} °C drop`,
      why: "Water-column imaging finds flares in the OUTER beams, and this layer is what refracts them. A cast that straddles it is worth more than one that averages across it.",
      caution: th !== null && th[2] >= 4,
    });
    facts.push({
      label: "Mixed layer",
      value: c.mld === null ? "none resolved" : fmtDepth(c.mld),
      why: "Above it the column is acoustically uniform; below it is where the cast needs its samples.",
    });
  }

  // --- heat flow ------------------------------------------------------------
  const floor = svp === null ? null : profileFloor(svp);
  const modelSeabed = svp?.cell.sb ?? null;
  const reachedBottom =
    floor !== null &&
    (modelSeabed !== null
      ? Math.abs(floor.depth - modelSeabed) < 1 // the model's own seabed
      : depth !== null && depth - floor.depth <= LEGACY_TOLERANCE_M);
  if (floor !== null && reachedBottom) {
    // POTENTIAL temperature, and the label says so. The fetch pulls `thetao`,
    // which is potential; WOA23's `t_an` — already in this payload — is IN SITU.
    // At 2,500 m those differ by ~0.21 °C of adiabatic compression, and printing
    // them side by side as one quantity manufactured a "0.2 °C model cold bias"
    // that does not exist: against the published deep-GoM potential temperature
    // (Ochoa et al. 2021, Θ = 4.053 °C) our own value agrees to 5 mK. Same class
    // of error as peak-vs-energy wave period. One label, one quantity.
    //
    // The climatology is not shown beside it for a second reason as well: below
    // 2,000 m GLO12 ASSIMILATES WOA/EN4 climatology as pseudo-observations to
    // suppress drift (PUM-001-024 §assimilated observations). The two are not
    // independent down there, so their agreement would prove nothing.
    facts.push({
      label: "Bottom-water potential temperature",
      value: `${floor.tempC.toFixed(2)} °C at ${fmtDepth(floor.depth)}`,
      why: "Potential temperature, not in-situ — they differ by about 0.2 °C at 2,500 m through adiabatic compression alone, so do not difference this against a probe reading without converting first.",
    });
    // What heat flow actually turns on is whether the bottom water has MOVED,
    // and the absolute above says nothing about that.
    // floor.depth, not the charted depth: inside this branch the profile HAS
    // reached bottom, so the model's own seabed is the depth in question — and
    // it is present even where NCEI returned nothing.
    if (inDeepGom(fc.lat, fc.lon, floor.depth)) {
      facts.push({
        label: "Bottom-water steadiness",
        value: "2–4 mK RMS, no seasonal cycle (measured)",
        why: "Below the 2,040 m Yucatán sill the deep Gulf is a ~100-year-residence reservoir. Moored CPIES records put near-bottom variability at 2–4 mK RMS with an annual harmonic under 1 mK — one to two orders of magnitude inside the ±0.03–0.15 °C band a heat-flow correction assumes. The residual is topographic Rossby waves, not season.",
      });
    } else {
      facts.push({
        label: "Bottom-water steadiness",
        value: null,
        absent: "not established at this location",
        why: "The 2–4 mK figure is a deep-Gulf measurement and does not travel. On the Guyana/Suriname margin and in the Angola Basin there are no Deep Argo floats and no public mooring array, so the transient a heat-flow correction turns on is simply unmeasured — and the model cannot stand in for it, being climatology-nudged below 2,000 m.",
        caution: true,
      });
    }
  } else {
    const short =
      floor !== null && depth !== null
        ? ` — it stops ${fmtDepth(depth - floor.depth)} above the charted depth`
        : "";
    facts.push({
      label: "Bottom-water temperature",
      value: null,
      absent: `the modelled column does not reach bottom here${short}`,
      why: "The profile now runs to the model's own seabed, so this is the deep-trench case: below the product's floor at 5,728 m there are no levels to have gone dry in, and the console does not guess at a temperature it has not been given.",
      caution: true,
    });
  }


  // --- the water column the wire passes through -----------------------------
  const cur = fc.current;
  const cs = cur?.speedKn[i] ?? null;
  const cd = cur?.dirDeg[i] ?? null;
  facts.push({
    label: "Surface current",
    value:
      cs === null
        ? null
        : `${cs.toFixed(1)} kn${cd === null ? "" : `, setting ${compass16(cd)} ${Math.round(cd)}°`}`,
    absent: "no surface current for this hour",
    why: "SURFACE ONLY. There is no depth-resolved current in this console, so nothing here describes the column the wire actually passes through — and in the deepwater Gulf a Loop Current eddy reaches 500–1,000 m at up to 4 kn. Read this as the top of the column, never as the column.",
    caution: true,
  });

  return facts;
}

/** Sea state at the hour, in the words a wire-and-A-frame operation uses. */
export function seepNote(fc: PointForecast, i: number): string {
  const wave = fc.waves?.height.find((m) => m.data[i] != null)?.data[i] ?? null;
  if (wave === null) return "";
  return (
    `Sea at this hour ${convLen(wave).toFixed(1)} ${lenUnit()}. ` +
    "Coring and heat-flow stations are wire work over the side — the workability read for the armed operation is on the main console; this panel only says what the water is doing."
  );
}
